import React, { useEffect, useMemo, useState } from 'react';
import {
  CircleHelp,
  Boxes,
  CirclePlus,
  ArrowUpDown,
  Warehouse,
  PackagePlus,
  Package,
  LineChart,
  ClipboardPlus,
  ClipboardList,
  Kanban,
  Users,
  UserRound,
  UserCog,
  Settings,
  SlidersHorizontal,
  ListChecks,
  ShieldUser,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X
} from 'lucide-react';

const renderTabIcon = (tabId) => {
  const iconProps = { size: 26, strokeWidth: 2 };
  switch (tabId) {
    case 'dashboard':
      return <Warehouse {...iconProps} />;
    case 'add':
      return <CirclePlus {...iconProps} />;
    case 'transactions':
      return <ArrowUpDown {...iconProps} />;
    case 'stock':
      return <Warehouse {...iconProps} />;
    case 'insights':
      return <LineChart {...iconProps} />;
    case 'raw_materials':
      return <Boxes {...iconProps} />;
    case 'product_add':
      return <PackagePlus {...iconProps} />;
    case 'products':
      return <Package {...iconProps} />;
    case 'order':
      return <ClipboardPlus {...iconProps} />;
    case 'orders':
      return <ClipboardList {...iconProps} />;
    case 'pipeline_board':
      return <Kanban {...iconProps} />;
    case 'staff':
      return <Users {...iconProps} />;
    case 'customers':
      return <UserRound {...iconProps} />;
    case 'contractors':
      return <UserCog {...iconProps} />;
    case 'settings':
      return <Settings {...iconProps} />;
    case 'settings_system':
      return <SlidersHorizontal {...iconProps} />;
    case 'settings_lists':
      return <ListChecks {...iconProps} />;
    case 'settings_users':
      return <ShieldUser {...iconProps} />;
    case 'help':
      return <CircleHelp {...iconProps} />;
    default:
      return null;
  }
};

function AdminDashboardTabs({
  activeTab,
  isAdminUser,
  isSidebarCollapsed,
  onStartTour,
  onToggleSidebar,
  orderedTabs,
  ordersEnabled,
  setSettingsTab,
  settingsTab,
  setActiveTab,
  visibleTabIds
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const tabItems = useMemo(() => {
    const items = [];
    if (visibleTabIds.has('stock')) {
      items.push({ id: 'stock', label: 'Stock', target: 'stock' });
    }
    if (visibleTabIds.has('insights')) {
      items.push({ id: 'insights', label: 'Insights', target: 'insights' });
    }

    if (visibleTabIds.has('add')) {
      items.push({ id: 'add', label: 'Add Movement', target: 'add' });
    }
    if (visibleTabIds.has('transactions')) {
      items.push({ id: 'transactions', label: 'Movement History', target: 'transactions' });
    }
    if (visibleTabIds.has('settings')) {
      items.push({
        id: 'raw_materials',
        label: 'Raw Materials',
        target: 'settings',
        active: activeTab === 'settings' && settingsTab === 'rawMaterials',
        onSelect: () => {
          setActiveTab('settings');
          setSettingsTab('rawMaterials');
        }
      });
    }

    if (visibleTabIds.has('product_add')) {
      items.push({ id: 'product_add', label: 'Add Product', target: 'product_add' });
    }
    if (visibleTabIds.has('products')) {
      items.push({ id: 'products', label: 'View Products', target: 'products' });
    }

    if (ordersEnabled && visibleTabIds.has('order')) {
      items.push({ id: 'order', label: 'Create Order', target: 'order' });
    }
    if (ordersEnabled && visibleTabIds.has('orders')) {
      items.push({ id: 'orders', label: 'View Orders', target: 'orders' });
    }

    if (ordersEnabled && visibleTabIds.has('pipeline_board')) {
      items.push({ id: 'pipeline_board', label: 'Workflow', target: 'pipeline_board' });
    }

    orderedTabs
      .filter((tab) => tab.mode === 'custom_form')
      .forEach((tab) => {
        items.push({ id: tab.id, label: tab.label, target: tab.id });
      });

    if (visibleTabIds.has('staff')) {
      items.push({ id: 'staff', label: 'Staff', target: 'staff' });
    }
    if (visibleTabIds.has('customers')) {
      items.push({ id: 'customers', label: 'Customers', target: 'customers' });
    }
    if (visibleTabIds.has('contractors')) {
      items.push({ id: 'contractors', label: 'Contractors', target: 'contractors' });
    }

    if (visibleTabIds.has('settings')) {
      items.push({
        id: 'settings_system',
        label: 'System Settings',
        target: 'settings',
        active: activeTab === 'settings' && settingsTab === 'system',
        onSelect: () => {
          setActiveTab('settings');
          setSettingsTab('system');
        }
      });
      items.push({
        id: 'settings_lists',
        label: 'Manage Lists',
        target: 'settings',
        active: activeTab === 'settings' && settingsTab === 'customLists',
        onSelect: () => {
          setActiveTab('settings');
          setSettingsTab('customLists');
        }
      });
      if (isAdminUser) {
        items.push({
          id: 'settings_users',
          label: 'User Access',
          target: 'settings',
          active: activeTab === 'settings' && settingsTab === 'users',
          onSelect: () => {
            setActiveTab('settings');
            setSettingsTab('users');
          }
        });
      }
    }
    if (typeof onStartTour === 'function') {
      items.push({
        id: 'help',
        label: 'Help',
        active: false,
        onSelect: onStartTour
      });
    }
    return items;
  }, [activeTab, isAdminUser, onStartTour, orderedTabs, ordersEnabled, setActiveTab, setSettingsTab, settingsTab, visibleTabIds]);

  const activateNavItem = (tab) => {
    if (typeof tab?.onSelect === 'function') {
      tab.onSelect();
      return;
    }
    if (tab?.target) {
      setActiveTab(tab.target);
    }
  };

  const handleTabsKeyDown = (event) => {
    const currentIndex = tabItems.findIndex((tab) => tab.active || activeTab === tab.target || activeTab === tab.id);
    if (currentIndex < 0) return;
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabItems.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabItems.length) % tabItems.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabItems.length - 1;

    activateNavItem(tabItems[nextIndex]);
  };

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onEscape = (event) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [mobileMenuOpen]);

  return (
    <nav className="admin-tabs-shell" aria-label="Main dashboard navigation">
      <button
        type="button"
        className="admin-mobile-hamburger"
        onClick={() => setMobileMenuOpen(true)}
        aria-label="Open navigation menu"
      >
        <Menu size={18} />
      </button>
      <aside className={`admin-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <button
          type="button"
          className="admin-sidebar-toggle"
          onClick={onToggleSidebar}
          aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isSidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
        <div className="admin-sidebar-tabs" role="tablist" aria-label="Main navigation tabs" onKeyDown={handleTabsKeyDown}>
          {tabItems.map((tab) => {
            const isActive = Boolean(tab.active) || activeTab === tab.id || activeTab === tab.target;
            return (
              <button
                key={`sidebar-${tab.id}`}
                id={`tab-${tab.id}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={tab.target ? `panel-${tab.target}` : undefined}
                tabIndex={isActive ? 0 : -1}
                className={isActive ? 'active' : ''}
                onClick={() => activateNavItem(tab)}
                title={tab.label}
              >
                <span className={`tab-icon tab-icon-${tab.id}`}>{renderTabIcon(tab.id)}</span>
                <span className="admin-tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </aside>

      {mobileMenuOpen && (
        <div className="admin-mobile-drawer-overlay" onClick={() => setMobileMenuOpen(false)}>
          <aside className="admin-mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="admin-mobile-drawer-header">
              <strong>Navigation</strong>
              <button
                type="button"
                className="admin-mobile-drawer-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <X size={16} />
              </button>
            </div>
            <div className="admin-mobile-drawer-tabs" role="tablist" aria-label="Mobile navigation tabs">
              {tabItems.map((tab) => {
                const isActive = Boolean(tab.active) || activeTab === tab.id || activeTab === tab.target;
                return (
                  <button
                    key={`mobile-drawer-${tab.id}`}
                    role="tab"
                    aria-selected={isActive}
                    className={isActive ? 'active' : ''}
                    onClick={() => {
                      activateNavItem(tab);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span className={`tab-icon tab-icon-${tab.id}`}>{renderTabIcon(tab.id)}</span>
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      )}
    </nav>
  );
}

export default AdminDashboardTabs;
